import { useState } from 'react';

import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import type { NormalizedApiError } from '@/shared/api';
import { getIntlLocale } from '@/shared/i18n';
import { ChannelBadge, formatClock } from '@/shared/ui/conversation';

import { conversationsKeys, usePostOperatorReply } from '../../hooks';
import { quotedPreview, resolveWhoLabel } from '../../model/messageDisplay';
import {
  OPERATOR_REPLY_CONFLICT_CODES,
  type ConversationChannel,
  type ConversationMessageView,
  type ConversationSessionView,
} from '../../model/types';

const TEXT_MAX_LENGTH = 4096;
const MAX_QUICK_REPLIES = 4;

function readReachableChannels(details: unknown): ConversationChannel[] {
  if (!details || typeof details !== 'object' || !('reachableChannels' in details)) {
    return [];
  }
  const raw = (details as { reachableChannels?: unknown }).reachableChannels;
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.filter((v): v is ConversationChannel => v === 'TELEGRAM' || v === 'MAX');
}

export interface OperatorComposerProps {
  conversationId: string;
  sessions: ConversationSessionView[];
  /**
   * The channel of the customer's LAST INBOUND message (`ConversationDetail.lastChannel`) — the hub's
   * own default when a reply omits `channel` (design §13.4; supersedes the earlier
   * `currentEpisode.openedByChannel` default). The selector preselects this and FOLLOWS it (re-derives
   * on every poll) until the operator touches the selector — see `pinnedChannel` below.
   */
  lastChannel: ConversationChannel;
  replyTo?: ConversationMessageView | null;
  onClearReplyTo: () => void;
  canViewPii: boolean;
  /**
   * The signed-in operator — threaded into `usePostOperatorReply` so the optimistic reply bubble reads
   * "Operator · you" at once (design §13.3), and used to name a quoted operator row in the reply-to
   * strip. `null`/absent renders the same as an older BFF with no ref (plain "Operator").
   */
  me?: { id: string; name?: string | null } | null;
  notify: (severity: 'success' | 'error' | 'info' | 'warning', message: string) => void;
}

/**
 * The operator's reply composer (design §7.5/§11.7/§13.4) — rendered only when the caller holds
 * `conversations.manage` AND `conversations.feature.operator-reply` (checked by `ConversationDetailPage`,
 * not here: this component's mere existence on the page already implies both).
 */
export function OperatorComposer({
  conversationId,
  sessions,
  lastChannel,
  replyTo,
  onClearReplyTo,
  canViewPii,
  me,
  notify,
}: OperatorComposerProps) {
  const { t } = useTranslation('conversations');
  const queryClient = useQueryClient();
  // `lastChannel` IS the hub's own default when `channel` is omitted (design §13.4) — the right
  // fallback for the hook's optimistic tail row too, whatever the operator's own selection resolves to.
  const mutation = usePostOperatorReply(conversationId, { fallbackChannel: lastChannel, me });

  // `null` = "following the hub's default" (re-derived below on every render, so a poll that moves
  // `lastChannel` moves the selection too); a channel = "pinned" once the operator has touched the
  // selector (or used a fallback's "Send via X instead") — never re-derived again this page (§13.4).
  const [pinnedChannel, setPinnedChannel] = useState<ConversationChannel | null>(null);

  function preferredChannel(): ConversationChannel {
    const preferred = sessions.find((s) => s.channel === lastChannel);
    if (preferred?.reachable) {
      return lastChannel;
    }
    return sessions.find((s) => s.reachable)?.channel ?? lastChannel;
  }

  const channel = pinnedChannel ?? preferredChannel();
  const followingDefault = pinnedChannel === null;
  const [text, setText] = useState('');
  const [quickReplies, setQuickReplies] = useState<string[]>([]);
  const [quickReplyDraft, setQuickReplyDraft] = useState('');
  const [replyIdRef, setReplyIdRef] = useState<string>(() => crypto.randomUUID());
  const [networkError, setNetworkError] = useState(false);
  const [fallbackAlert, setFallbackAlert] = useState<
    { kind: 'unreachable'; reachableChannels: ConversationChannel[] } | { kind: 'notFound' } | null
  >(null);

  const anyReachable = sessions.some((s) => s.reachable);
  const singleChannel = sessions.length <= 1;
  // A confirmed-empty `reachableChannels` on a 409 means "blocked everywhere" server-side — treat it
  // the same as the client already knowing this from `sessions`, not as a per-channel fallback alert
  // (design §11.7: "the composer switches to the disabled state above").
  const confirmedNoChannelLeft =
    fallbackAlert?.kind === 'unreachable' && fallbackAlert.reachableChannels.length === 0;
  const noChannelLeft = !anyReachable || confirmedNoChannelLeft;

  function mintNewReplyId() {
    const next = crypto.randomUUID();
    setReplyIdRef(next);
    return next;
  }

  function send(closeConversation: boolean, overrideChannel?: ConversationChannel) {
    const trimmed = text.trim();
    if (!trimmed || trimmed.length > TEXT_MAX_LENGTH || mutation.isPending) {
      return;
    }
    const effectiveChannel = overrideChannel ?? channel;
    setNetworkError(false);
    setFallbackAlert(null);

    mutation.mutate(
      {
        replyId: replyIdRef,
        parts: [{ type: 'TEXT', text: trimmed, format: 'PLAIN' }],
        actions: quickReplies.map((label) => ({
          type: 'QUICK_REPLY' as const,
          label,
          value: label,
        })),
        // Sent only when it differs from the hub's own default (design §13.4) — an equal value is
        // omitted so the hub applies its default at send time and the two can never silently disagree.
        channel: effectiveChannel === lastChannel ? undefined : effectiveChannel,
        replyToMessageId: replyTo?.id ?? null,
        closeConversation,
      },
      {
        // The optimistic `QUEUED` tail row and the surrounding invalidation now live in
        // `usePostOperatorReply`'s own `onSuccess` (A5, WP5 review) — TanStack Query v5 runs it before
        // this call-site one, so it is the only place that can insert the row before anything might
        // refetch the tail out from under it. This callback is left with component-local cleanup only:
        // there is exactly one write path to the query cache.
        onSuccess: () => {
          setText('');
          setQuickReplies([]);
          onClearReplyTo();
          mintNewReplyId();
          notify('success', t('composer.accepted'));
        },
        onError: (error: NormalizedApiError) => {
          if (error.status === null) {
            setNetworkError(true);
            return; // keep the same replyId — a retry re-posts it unchanged
          }
          if (
            error.status === 409 &&
            error.code === OPERATOR_REPLY_CONFLICT_CODES.sessionUnreachable
          ) {
            const reachableChannels = readReachableChannels(error.details);
            setFallbackAlert({ kind: 'unreachable', reachableChannels });
            mintNewReplyId();
            void queryClient.invalidateQueries({
              queryKey: conversationsKeys.detail(conversationId),
            });
            return;
          }
          if (
            error.status === 409 &&
            error.code === OPERATOR_REPLY_CONFLICT_CODES.sessionNotFound
          ) {
            // Unlike SESSION_UNREACHABLE (a dedicated "send via a different channel" action — a
            // genuinely different physical send, hence its own fresh id), nothing here has been
            // offered or sent yet: the operator picks a channel from the same selector and retries
            // the SAME message, which keeps its `replyId` (B4, WP5 review — a new id was minted here
            // even though this is not a distinct send attempt).
            setFallbackAlert({ kind: 'notFound' });
            void queryClient.invalidateQueries({
              queryKey: conversationsKeys.detail(conversationId),
            });
            return;
          }
          mintNewReplyId();
          notify('error', error.message);
        },
      },
    );
  }

  function addQuickReply() {
    const value = quickReplyDraft.trim();
    if (!value || quickReplies.length >= MAX_QUICK_REPLIES) {
      return;
    }
    setQuickReplies((current) => [...current, value]);
    setQuickReplyDraft('');
  }

  const overLimit = text.length > TEXT_MAX_LENGTH;
  const sendDisabled = !text.trim() || overLimit || noChannelLeft || mutation.isPending;

  // Stable ids for the channel selector's accessible name/description (design §13.8 — the default
  // is announced with the choice, not just shown as a sighted-only caption next to the group).
  const replyViaLabelId = `composer-reply-via-${conversationId}`;
  const defaultChannelHintId = `composer-default-channel-hint-${conversationId}`;
  const showDefaultChannelHint = followingDefault && channel === lastChannel;

  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderTop: 2, borderTopColor: 'divider' }}>
      <Stack spacing={1}>
        {singleChannel ? (
          <Typography variant="caption" color="text.secondary">
            {t('composer.singleChannel', { channel: t(`channel.${channel}`, channel) })}
          </Typography>
        ) : (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography id={replyViaLabelId} variant="caption" color="text.secondary">
              {t('composer.replyVia')}
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={channel}
              aria-labelledby={replyViaLabelId}
              aria-describedby={showDefaultChannelHint ? defaultChannelHintId : undefined}
              onChange={(_e, next: ConversationChannel | null) => {
                // Touching the selector pins the choice for the rest of the page's lifetime — it no
                // longer follows `lastChannel` on a later poll (design §13.4).
                if (next) setPinnedChannel(next);
              }}
            >
              {sessions.map((session) => (
                <ToggleButton
                  key={session.channel}
                  value={session.channel}
                  disabled={!session.reachable}
                  title={session.reachable ? undefined : t('composer.channelBlocked')}
                >
                  <ChannelBadge
                    channel={session.channel}
                    blocked={!session.reachable}
                    variant="outlined"
                  />
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            {showDefaultChannelHint ? (
              <Typography id={defaultChannelHintId} variant="caption" color="text.secondary">
                {t('composer.defaultChannelHint')}
              </Typography>
            ) : null}
          </Stack>
        )}

        {replyTo ? (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ borderLeft: '3px solid', borderColor: 'primary.main', pl: 1, py: 0.25 }}
          >
            <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1 }}>
              {t('composer.replyingTo', {
                who: resolveWhoLabel(replyTo, t, me?.id),
                time: formatClock(replyTo.createdAt, getIntlLocale()),
                text: quotedPreview(replyTo, canViewPii, t),
              })}
            </Typography>
            <Button
              size="small"
              onClick={onClearReplyTo}
              aria-label={t('composer.cancelReply')}
              startIcon={<CloseRoundedIcon fontSize="inherit" />}
            >
              {t('composer.cancelReply')}
            </Button>
          </Stack>
        ) : null}

        {noChannelLeft ? (
          <Alert severity="error">{t('composer.noChannelLeft')}</Alert>
        ) : fallbackAlert?.kind === 'unreachable' ? (
          <Alert
            severity="warning"
            action={
              fallbackAlert.reachableChannels.length > 0 ? (
                <Stack direction="row" spacing={1}>
                  {fallbackAlert.reachableChannels.map((target) => (
                    <Button
                      key={target}
                      size="small"
                      color="inherit"
                      onClick={() => {
                        // A "Send via X instead" pick is a pinned choice too (design §13.4) — it must
                        // not silently revert to following `lastChannel` on the next poll.
                        setPinnedChannel(target);
                        setFallbackAlert(null);
                        send(false, target);
                      }}
                    >
                      {t('composer.sendViaInstead', { channel: t(`channel.${target}`, target) })}
                    </Button>
                  ))}
                </Stack>
              ) : undefined
            }
          >
            {t('composer.unreachableOn', { channel: t(`channel.${channel}`, channel) })}
          </Alert>
        ) : fallbackAlert?.kind === 'notFound' ? (
          <Alert severity="warning">
            {t('composer.sessionNotFound', { channel: t(`channel.${channel}`, channel) })}
          </Alert>
        ) : null}

        {networkError ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => send(false)}>
                {t('composer.retry')}
              </Button>
            }
          >
            {t('composer.networkError')}
          </Alert>
        ) : null}

        <TextField
          multiline
          minRows={2}
          maxRows={8}
          fullWidth
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t('composer.placeholder')}
          disabled={noChannelLeft}
          error={overLimit}
          // `aria-label` passed directly to `TextField` lands on the outer root `<div>`, not the
          // `<textarea>` — it must go through `slotProps.htmlInput` to actually label the control
          // `getByLabelText` (and a screen reader) resolves.
          slotProps={{ htmlInput: { 'aria-label': t('composer.textAria') } }}
          onKeyDown={(e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
              e.preventDefault();
              send(false);
            }
          }}
        />

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
          <Typography variant="caption" color="text.secondary">
            {t('composer.quickReplies')}:
          </Typography>
          {quickReplies.map((label, index) => (
            <Chip
              key={`${label}-${index}`}
              label={label}
              size="small"
              onDelete={() => setQuickReplies((current) => current.filter((_, i) => i !== index))}
            />
          ))}
          {quickReplies.length < MAX_QUICK_REPLIES ? (
            <TextField
              size="small"
              variant="standard"
              label={t('composer.addQuickReply')}
              placeholder={t('composer.quickReplyPlaceholder')}
              value={quickReplyDraft}
              disabled={noChannelLeft}
              inputProps={{ maxLength: 64 }}
              onChange={(e) => setQuickReplyDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addQuickReply();
                }
              }}
              sx={{ width: 160 }}
            />
          ) : null}
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {t('composer.quickRepliesHelper')}
        </Typography>

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
        >
          <Typography variant="caption" color="text.secondary">
            {t('composer.counter', { length: text.length, max: TEXT_MAX_LENGTH })} ·{' '}
            {t('composer.shortcut')}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              disabled={sendDisabled}
              onClick={() => send(true)}
              title={t('composer.sendAndCloseTooltip')}
            >
              {t('composer.sendAndClose')}
            </Button>
            <Button
              variant="contained"
              disabled={sendDisabled}
              onClick={() => send(false)}
              startIcon={
                mutation.isPending ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <SendRoundedIcon />
                )
              }
            >
              {t('composer.send')}
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </Paper>
  );
}
